import { Button } from "@chakra-ui/react";
import { FunctionComponent, ReactElement, useState } from "react";

// Components
import DataTable from "../../../shared/DataTable/DataTable";
import AddUserModal from "../AddUserModal";

// Utils
import { IParams } from "../../../shared/DataTable/hooks/useSearchParams";
import { DataTableColumn, KVP, PaginatedApiResponse, SortDirection } from "../../../utils/types";

// Hooks
import usePermission from "../../../hooks/usePermission";
import { UserRole } from "../../../utils/consts";
import { USERS_TABS, UserTabKey } from "../constants";

// Constants
const DEFAULT_PARAMS: Partial<IParams> = {
	sortDirection: SortDirection.Asc,
	sortField: "email"
};

interface UsersIndexTableProps {
	tab: UserTabKey;
	usersData: PaginatedApiResponse;
	columns: Array<DataTableColumn>;
	searchParams: URLSearchParams;
	defaultParams?: Partial<IParams>;
	isLoading?: boolean;
	handleFilterChange: (cacheKey: string) => void;
	handleToggleLoading: (isPageLoading: boolean) => void;
}

const UsersIndexTable: FunctionComponent<UsersIndexTableProps> = ({
	tab,
	usersData,
	columns,
	searchParams,
	defaultParams,
	isLoading,
	handleFilterChange,
	handleToggleLoading
}) => {
	const { hasGeneralManagerAccess, roles } = usePermission();
	const [isAddUserOpen, setIsAddUserOpen] = useState<boolean>(false);
	const [cachedKey, setCachedKey] = useState<string>("");

	const refreshData = () => {
		handleFilterChange(cachedKey);
	};

	const getCustomHeader = (): ReactElement => (
		<>
			{isAddUserOpen && (
				<AddUserModal
					isOpen={isAddUserOpen}
					onClose={() => setIsAddUserOpen(false)}
					onContinue={() => {
						setIsAddUserOpen(false);
						refreshData();
					}}
				/>
			)}
			{tab === USERS_TABS.Providers && (hasGeneralManagerAccess || roles.includes(UserRole.ClaimsNetworkManager)) && (
				<Button
					marginRight={4}
					size="sm"
					onClick={() => setIsAddUserOpen(true)}
					isDisabled={false}
					colorScheme="orange"
					padding={2}>
					Add User
				</Button>
			)}
		</>
	);

	const getCustomSelectedActions = (): ReactElement => <></>;

	const getCustomActionMenuActions = (userRow: KVP): ReactElement | undefined => {
		// TODO: Add user action menu items like Edit, Disable, etc.
		return undefined;
	};

	return (
		<DataTable
			data={usersData}
			columns={columns}
			refetchTrigger={(cacheKey) => {
				handleFilterChange(cacheKey);
				setCachedKey(cacheKey);
			}}
			customHeaderActions={getCustomHeader()}
			customSelectedActions={getCustomSelectedActions}
			customActionsMenuContents={getCustomActionMenuActions}
			defaultParams={defaultParams || DEFAULT_PARAMS}
			existingParams={searchParams}
			shouldUpdateUrl={true}
			isLoading={isLoading}
		/>
	);
};

export default UsersIndexTable;
